import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Clock, AlertTriangle, CreditCard, ArrowRight, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialExpiredPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get info from location state or default
  const { 
    institutionName = 'Tu institución',
    planName = 'Tu plan',
    expiredDate = null,
    daysOverdue = 0
  } = location.state || {};

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleContactSales = () => {
    // Scroll to contact section on landing page
    navigate('/#contacto');
  };

  const handleUpgrade = () => {
    navigate('/#planes');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        <Card className="bg-slate-800 border-slate-700 overflow-hidden">
          {/* Warning Banner */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-4">
            <div className="flex items-center justify-center gap-3">
              <Lock className="w-8 h-8 text-white" />
              <h1 className="text-xl font-bold text-white">Período de Prueba Finalizado</h1>
            </div>
          </div>

          <CardContent className="p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-10 h-10 text-orange-400" />
              </div>
            </div>

            {/* Message */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                Tu acceso ha sido suspendido
              </h2>
              <p className="text-slate-400 mb-4">
                El período de prueba gratuita de <span className="text-white font-medium">{institutionName}</span> ha finalizado.
              </p>
              
              {expiredDate && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700/50 rounded-lg text-sm">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <span className="text-slate-300">
                    Expiró el {formatDate(expiredDate)}
                    {daysOverdue > 0 && (
                      <span className="text-orange-400 ml-1">
                        (hace {daysOverdue} días)
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* What's blocked */}
            <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Funcionalidades bloqueadas:
              </h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Creación de nuevas simulaciones
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Acceso al dashboard de instructores
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Gestión de alumnos y cursos
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Generación de informes
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button 
                onClick={handleUpgrade}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-6"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Activar Suscripción
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleContactSales}
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Contactar a Ventas
              </Button>

              <div className="pt-4 border-t border-slate-700">
                <button 
                  onClick={handleLogout}
                  className="w-full text-slate-500 hover:text-slate-300 text-sm transition-colors"
                >
                  Cerrar sesión
                </button>
              </div>
            </div>

            {/* Help text */}
            <p className="text-center text-slate-500 text-xs mt-6">
              ¿Necesitas ayuda? Escríbenos a{' '}
              <a href="mailto:soporte@simuclinic.cl" className="text-cyan-400 hover:underline">
                soporte@simuclinic.cl
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Additional info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center"
        >
          <p className="text-slate-500 text-sm">
            Tus datos están seguros y se mantendrán disponibles una vez actives tu suscripción.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
